import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import './Wellness.less';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { DateTime } from 'luxon';
import { Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import BloodPressureChart from './Charts/BloodPressureChart';
import PulseOximeterChart from './Charts/PulseOximeterChart';
import WeightScaleChart from './Charts/WeightScaleChart';
import GlucoseMeterChart from './Charts/GlucoseMeterChart';
import BodyTemperatureChart from './Charts/BodyTemperatureChart';
import { unitTitleData } from './WellnessUtils';
import { useSelector } from 'react-redux';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

function parseHour(formattedTime) {
  const [hm, ampm = 'AM'] = formattedTime.split(' ');
  if (!hm) return 0;
  const [hh, mm = '0'] = hm.split(':');
  let hour = parseInt(hh, 10);
  const minute = parseInt(mm, 10) || 0;

  if (ampm.toUpperCase() === 'PM' && hour < 12) {
    hour += 12;
  } else if (ampm.toUpperCase() === 'AM' && hour === 12) {
    hour = 0;
  }
  // For finer spacing, do hour + minute/60
  return hour;
}

const WellnessHistoryInsightChart = ({
  modalData = [],
  userMeasurement = 'blood_pressure',
  modalTitle = 'blood_pressure',
  timezone = 'America/New_York',
  selectedRange,
  setSelectedRange,
  is24hoursformat,
  userDefinedMeasurement,
  insightStartDateTime,
  insightEndDateTime,
  insightData,
  insightLoading,
  getTranslatedLabel,
}) => {
  const trans = useSelector(translator);
  const [summaryData, setSummaryData] = useState({});
  const [graphData, setGraphData] = useState(null);
  const [graphOptions, setGraphOptions] = useState({});
  const [loading, setLoading] = useState(true); // Spinner control
  const [dataValues, setDataValues] = useState([]);
  const [maxDataValue, setMaxDataValue] = useState(0);
  const [chartData, setChartData] = useState(null);

  /**
   * Format numeric values to a max of 5 characters.
   */
  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number' && !Number.isInteger(value)) {
      const roundedValue = Math.round(value * 100) / 100; // round to 2 decimal places
      return roundedValue.toString().length > 5
        ? roundedValue.toFixed(1).slice(0, 5)
        : roundedValue.toFixed(1).toString();
    } else {
      return value;
    }
  };

  const getUniqData = (data) => {
    return data.reduce((acc, item) => {
      const ts = new Date(item.utc_timestamp_inms);
      const key = DateTime.fromJSDate(ts)
        .toUTC() // normalize timezone
        .toFormat('yyyy-MM-dd HH:mm');

      // keep latest for that minute
      if (!acc[key] || ts > new Date(acc[key].actualTimestamp)) {
        acc[key] = item;
      }

      return acc;
    }, {});
  };

  const userTimezone = timezone;
  const todayStart = moment().tz(userTimezone).subtract(24, 'hours').valueOf();
  const todayEnd = moment().tz(userTimezone).valueOf();
  const yesterdayStart = moment().tz(userTimezone).subtract(1, 'days').startOf('day').valueOf();
  const yesterdayEnd = moment().tz(userTimezone).subtract(1, 'days').endOf('day').valueOf();
  const lastWeekStart = moment().tz(userTimezone).subtract(7, 'days').startOf('day').valueOf();
  const lastWeekEnd = moment().tz(userTimezone).endOf('day').valueOf();
  const lastMonthStart = moment().tz(userTimezone).subtract(30, 'days').startOf('day').valueOf();
  const lastMonthEnd = moment().tz(userTimezone).endOf('day').valueOf();
  const lastNinetyDaysStart = moment()
    .tz(userTimezone)
    .subtract(90, 'days')
    .startOf('day')
    .valueOf();
  const lastNinetyDaysEnd = moment().tz(userTimezone).endOf('day').valueOf();

  useEffect(() => {
    if (!insightData || !insightData.length) {
      // no data => no summary
      const r = {};
      if (modalTitle === 'glucose_meter') {
        r['glucose_concentration'] = ' - ';
      }
      if (modalTitle === 'weight_scale') {
        r['weight'] = ' - ';
      }
      if (modalTitle === 'thermometer') {
        r['temperature'] = ' - ';
      }
      if (modalTitle === 'pulse_oximeter') {
        r['oxygen'] = '-';
        r['pulse_rate'] = ' - ';
      }
      if (modalTitle === 'blood_pressure') {
        r['systolic'] = ' - ';
        r['diastolic'] = ' - ';
        r['pulse'] = ' - ';
      }
      setSummaryData(r);
      return;
    }

    let filteredData = getUniqData(insightData);

    filteredData = Object.values(filteredData);

    const todayData = filteredData.filter((item) => {
      const ts = moment(item.utc_timestamp_inms).tz(userTimezone).valueOf();
      return ts >= todayStart && ts <= todayEnd && item.data && typeof item.data === 'object';
    });

    const yesterdayData = filteredData.filter((item) => {
      const ts = moment(item.utc_timestamp_inms).tz(userTimezone).valueOf();
      return (
        ts >= yesterdayStart && ts <= yesterdayEnd && item.data && typeof item.data === 'object'
      );
    });

    const lastWeekData = filteredData.filter((item) => {
      const ts = moment(item.utc_timestamp_inms).tz(userTimezone).valueOf();
      return ts >= lastWeekStart && ts <= lastWeekEnd && item.data && typeof item.data === 'object';
    });

    const lastMonthData = filteredData.filter((item) => {
      const ts = moment(item.utc_timestamp_inms).tz(userTimezone).valueOf();
      return (
        ts >= lastMonthStart && ts <= lastMonthEnd && item.data && typeof item.data === 'object'
      );
    });

    const lastNinetyDaysData = filteredData.filter((item) => {
      const ts = moment(item.utc_timestamp_inms).tz(userTimezone).valueOf();
      return (
        ts >= lastNinetyDaysStart &&
        ts <= lastNinetyDaysEnd &&
        item.data &&
        typeof item.data === 'object'
      );
    });

    const metrics =
      filteredData[0] && filteredData[0].data ? Object.keys(filteredData[0].data) : [];

    const results = {};

    if (selectedRange === 'today') {
      const arr = todayData.map((d) => d.data);
      metrics.forEach((m) => {
        const vals = arr.map((o) => o[m]).filter((v) => v != null);
        if (vals.length) {
          results[m] = {
            HIGH: formatValue(Math.max(...vals)),
            LOW: formatValue(Math.min(...vals)),
            // AVG: formatValue(
            //   Math.round((vals.reduce((x, y) => x + y, 0) / vals.length) * 100) /
            //   100
            // ),
          };
        } else {
          results[m] = '-';
        }
      });
    } else if (selectedRange === 'yesterday') {
      const arr = yesterdayData.map((d) => d.data);
      metrics.forEach((m) => {
        const vals = arr.map((o) => o[m]).filter((v) => v != null);
        if (vals.length) {
          results[m] = {
            HIGH: formatValue(Math.max(...vals)),
            LOW: formatValue(Math.min(...vals)),
            // AVG: formatValue(
            //   Math.round((vals.reduce((x, y) => x + y, 0) / vals.length) * 100) /
            //   100
            // ),
          };
        } else {
          results[m] = '-';
        }
      });
    } else if (selectedRange === 'lastweek') {
      const arr = lastWeekData.map((d) => d.data);
      metrics.forEach((m) => {
        const vals = arr.map((o) => o[m]).filter((v) => v != null);
        if (vals.length) {
          results[m] = {
            HIGH: formatValue(Math.max(...vals)),
            LOW: formatValue(Math.min(...vals)),
            // AVG: formatValue(
            //   Math.round((vals.reduce((x, y) => x + y, 0) / vals.length) * 100) /
            //   100
            // ),
          };
        } else {
          results[m] = '-';
        }
      });
    } else if (selectedRange === 'lastmonth') {
      const arr = lastMonthData.map((d) => d.data);
      metrics.forEach((m) => {
        const vals = arr.map((o) => o[m]).filter((v) => v != null);
        if (vals.length) {
          results[m] = {
            HIGH: formatValue(Math.max(...vals)),
            LOW: formatValue(Math.min(...vals)),
            // AVG: formatValue(
            //   Math.round((vals.reduce((x, y) => x + y, 0) / vals.length) * 100) /
            //   100
            // ),
          };
        } else {
          results[m] = '-';
        }
      });
    } else if (selectedRange === 'all') {
      const arr = lastNinetyDaysData.map((d) => d.data);
      metrics.forEach((m) => {
        const vals = arr.map((o) => o[m]).filter((v) => v != null);
        if (vals.length) {
          results[m] = {
            HIGH: formatValue(Math.max(...vals)),
            LOW: formatValue(Math.min(...vals)),
          };
        } else {
          results[m] = '-';
        }
      });
    } else {
      metrics.forEach((m) => {
        results[m] = '-';
      });
    }
    setSummaryData(results);
  }, [insightData, timezone, modalTitle, selectedRange]);

  useEffect(() => {
    // If no data => skip
    if (!insightData || !insightData.length) {
      setLoading(false); // Done loading, but no data
      setGraphData({ labels: [], datasets: [] });
      setDataValues([]);
      setMaxDataValue(0);
      return;
    }

    let uniqData = getUniqData(insightData);

    uniqData = Object.values(uniqData);

    let filteredData = [...uniqData];
    const userTimezone = timezone;

    if (selectedRange === 'today') {
      filteredData = uniqData.filter((item) => {
        const ts = moment(item.utc_timestamp_inms).tz(userTimezone).valueOf();
        return ts >= todayStart && ts <= todayEnd;
      });
    } else if (selectedRange === 'yesterday') {
      filteredData = uniqData.filter((item) => {
        const ts = moment(item.utc_timestamp_inms).tz(userTimezone).valueOf();
        return ts >= yesterdayStart && ts <= yesterdayEnd;
      });
    } else if (selectedRange === 'lastweek') {
      filteredData = uniqData.filter((item) => {
        const ts = moment(item.utc_timestamp_inms).tz(userTimezone).valueOf();
        return ts >= lastWeekStart && ts <= lastWeekEnd;
      });
    } else if (selectedRange === 'lastmonth') {
      filteredData = uniqData.filter((item) => {
        const ts = moment(item.utc_timestamp_inms).tz(userTimezone).valueOf();
        return ts >= lastMonthStart && ts <= lastMonthEnd;
      });
    } else if (selectedRange === 'all') {
      filteredData = uniqData.filter((item) => {
        const ts = moment(item.utc_timestamp_inms).tz(userTimezone).valueOf();
        return ts >= lastNinetyDaysStart && ts <= lastNinetyDaysEnd;
      });
    }

    // Sort data ascending by timestamp
    const sorted = filteredData.sort(
      (a, b) => new Date(a.utc_timestamp_inms).getTime() - new Date(b.utc_timestamp_inms).getTime()
    );

    let daysList = [];
    if (selectedRange === 'lastweek') {
      for (let i = 6; i >= 0; i--) {
        daysList.push(moment().tz(timezone).subtract(i, 'days').format('ddd M/D'));
      }
    } else if (selectedRange === 'today') {
      daysList = [moment().tz(timezone).format('ddd M/D')];
    } else if (selectedRange === 'yesterday') {
      daysList = [moment().tz(timezone).subtract(1, 'day').format('ddd M/D')];
    } else if (selectedRange === 'lastmonth') {
      for (let i = 29; i >= 0; i--) {
        daysList.push(moment().tz(timezone).subtract(i, 'days').format('ddd M/D'));
      }
    } else if (selectedRange === 'all') {
      for (let i = 90; i >= 0; i--) {
        daysList.push(moment().tz(timezone).subtract(i, 'days').format('ddd M/D'));
      }
    }

    // Find relevant metrics
    const relevantMetrics = Object.keys(unitTitleData[userMeasurement] || {});
    const metrics = relevantMetrics.filter((m) =>
      sorted.some((item) => item.data && item.data.hasOwnProperty(m))
    );
    // Fallback if none found
    if (!metrics.length && sorted[0]?.data) {
      metrics.push(...Object.keys(sorted[0].data));
    }

    // Convert each item to consistent date format
    const consistentData = sorted.map((entry) => {
      const dayStr = moment(entry.utc_timestamp_inms).tz(timezone).format('ddd M/D');
      return {
        ...entry,
        consistentDate: dayStr,
      };
    });
    setChartData(consistentData);
    // Build datasets
    const datasets = metrics.map((metric) => {
      const dailyVals = daysList.map((dayStr) => {
        const sameDayItems = consistentData.filter((item) => item.consistentDate === dayStr);
        if (!sameDayItems.length) return null;
        const latest = sameDayItems[sameDayItems.length - 1];
        return latest.data[metric] ?? null;
      });

      function getRandomDarkColor() {
        // Keep each RGB component between 0 and 127 for a darker shade
        const r = Math.floor(Math.random() * 128);
        const g = Math.floor(Math.random() * 128);
        const b = Math.floor(Math.random() * 128);

        return `rgb(${r}, ${g}, ${b})`;
      }

      return {
        label: getTranslatedLabel(unitTitleData[userMeasurement][metric]) || metric,
        data: dailyVals,
        borderColor:
          metric === 'systolic'
            ? '#FF6B6B'
            : metric === 'diastolic'
              ? '#36A2EB'
              : metric === 'pulse'
                ? '#4BC0C0'
                : getRandomDarkColor(),
        backgroundColor: 'white',
        borderWidth: 1.5,
        pointRadius: 5,
        pointHoverRadius: 6,
        tension: 0.4,
      };
    });

    // Build graph data
    const newGraphData = {
      labels: daysList,
      datasets,
    };

    setGraphData(newGraphData);

    // max y-value
    const allVals = [];
    datasets.forEach((ds) => {
      ds.data.forEach((val) => {
        if (val != null && !isNaN(val)) {
          allVals.push(val);
        }
      });
    });
    setDataValues(allVals);
    setMaxDataValue(allVals.length ? Math.max(...allVals) : 0);

    // Now done loading, we have valid data
    setLoading(false);
  }, [insightData, userMeasurement, timezone, selectedRange]);

  /************************************************************
   * 3) Final transform: Build x-axis in hours from day index
   ************************************************************/
  useEffect(() => {
    if (!graphData || !Array.isArray(graphData.labels) || !Array.isArray(graphData.datasets)) {
      setGraphOptions({});
      return;
    }

    const finalLabels = graphData.labels;
    const finalDatasets = graphData.datasets;

    // dayIndexMap => "Mon 5/01" => 0..6
    const dayIndexMap = {};
    finalLabels.forEach((dayStr, idx) => {
      dayIndexMap[dayStr] = idx;
    });

    const filteredData = getUniqData(insightData);

    // Build dayTimeMap => { "Mon 5/01": { hour, formattedTime } }
    const sorted = [...Object.values(filteredData)].sort(
      (a, b) => new Date(a.utc_timestamp_inms).getTime() - new Date(b.utc_timestamp_inms).getTime()
    );
    const dayTimeMap = {};
    sorted.forEach((entry) => {
      const dayStr = moment(entry.utc_timestamp_inms).tz(timezone).format('ddd M/D');
      if (!dayIndexMap.hasOwnProperty(dayStr)) return;
      const hh = parseHour(entry.formatted_time || '');
      dayTimeMap[dayStr] = {
        hour: hh,
        formattedTime: entry.formatted_time,
      };
    });

    // Convert each dataset => x,y
    const newDatasets = finalDatasets.map((ds) => {
      const newData = ds.data.map((val, i) => {
        const dayStr = finalLabels[i];
        if (val == null) {
          return { x: i * 24, y: null, day: dayStr, time: '' };
        }
        const dtObj = dayTimeMap[dayStr] || { hour: 0, formattedTime: '' };
        return {
          x: i * 24 + dtObj.hour,
          y: val,
          day: dayStr,
          time: dtObj.formattedTime,
        };
      });
      return {
        ...ds,
        data: newData,
        showLine: true,
        spanGaps: false,
      };
    });

    const finalChartData = {
      datasets: newDatasets,
    };

    // x-range => (7-1)*24 = 144
    const maxX =
      selectedRange === 'lastweek' || selectedRange === 'lastmonth' || selectedRange === 'all'
        ? (finalLabels.length - 1) * 24
        : 24;

    const finalOptions = {
      responsive: true,
      maintainAspectRatio: false,
      // animation: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            font: {
              size: 14,
              family: 'Arial',
            },
            color: '#005D98',
            usePointStyle: true,
          },
        },
        tooltip: {
          enabled: true,
          callbacks: {
            // Title => day label
            title: (items) => {
              const p = items[0]?.raw;
              return p?.day || '';
            },
            // Label => "Systolic mmHg (10:58 AM): 129"
            label: (ctx) => {
              const p = ctx.raw;
              if (p?.y == null) return `${ctx.dataset.label}: N/A`;
              return `${ctx.dataset.label} (${p.time}): ${formatValue(p.y)}`;
            },
          },
        },
        datalabels: {
          display: false,
        },
      },
      scales: {
        x: {
          type: 'linear',
          min: 0,
          max: maxX,
          grid: {
            color: '#005D98',
            drawTicks: false,
          },
          ticks: {
            stepSize: 24,
            color: '#005D98',
            font: {
              size: 12,
              family: 'Arial',
            },
            callback: function (value) {
              if (selectedRange === 'lastweek') {
                const i = Math.floor(value / 24);
                return finalLabels[i] || '';
              } else if (selectedRange === 'lastmonth') {
                const i = Math.floor(value / 24);
                return i % 3 === 0 ? finalLabels[i] || '' : '';
              } else if (selectedRange === 'all') {
                const i = Math.floor(value / 24);
                return i % 9 === 0 ? finalLabels[i] || '' : '';
              } else {
                return finalLabels[0]; // Only one label for today/yesterday
              }
            },
          },
          border: {
            color: '#005D98',
            dash: [3, 5],
          },
        },
        y: {
          grid: {
            color: '#005D98',
            lineWidth: 1,
            drawTicks: false,
            z: 100,
          },
          ticks: {
            color: '#005D98',
            font: {
              size: 12,
              family: 'Arial',
            },
          },
          border: {
            dash: [3, 5],
          },
          beginAtZero: true,
          suggestedMax: maxDataValue + 11,
        },
      },
    };

    setGraphOptions(finalOptions);
    setGraphData(finalChartData);
  }, [graphData, insightData, maxDataValue, selectedRange]);

  const getDateFormatByLocation = (userTimezone) => {
    const timeZoneArray = userTimezone.toUpperCase().split('/');
    const timeZone = timeZoneArray[0];
    const timeZoneArea = timeZoneArray[1];
    if (timeZone === 'AMERICA' || (timeZone === 'PACIFIC' && timeZoneArea === 'HONOLULU')) {
      return 'MMM D';
    } else return 'D MMM';
  };

  return (
    <div className="modal-insight-chart-tab-content">
      <div className="modal-insight-chart-container">
        {/* SUMMARY CARD */}
        <div className="modal-insight-chart-summary-card">
          <div className="modal-insight-chart-summary-block">
            <div className="modal-insight-chart-summary-title">{trans('SUMMARY')}</div>
            <div className="wellness-insight-chart-view-options-container">
              <div className="wellness-insight-chart-view-block">
                <div
                  className={`wellness-insight-chart-view-option ${selectedRange === 'today' ? 'selected-option' : ''}`}
                  onClick={() => setSelectedRange('today')}
                >
                  <span
                    className={`wellness-insight-chart-view-option-label ${selectedRange === 'today' ? 'selected' : ''}`}
                  >
                    {trans('DAY')}
                  </span>
                </div>
                <div
                  className={`wellness-insight-chart-view-option ${selectedRange === 'lastweek' ? 'selected-option' : ''}`}
                  onClick={() => setSelectedRange('lastweek')}
                >
                  <span
                    className={`wellness-insight-chart-view-option-label ${selectedRange === 'lastweek' ? 'selected' : ''}`}
                  >
                    {trans('WEEK')}
                  </span>
                </div>
                <div
                  className={`wellness-insight-chart-view-option ${selectedRange === 'lastmonth' ? 'selected-option' : ''}`}
                  onClick={() => setSelectedRange('lastmonth')}
                >
                  <span
                    className={`wellness-insight-chart-view-option-label ${selectedRange === 'lastmonth' ? 'selected' : ''}`}
                  >
                    {trans('MONTH')}
                  </span>
                </div>
                <div
                  className={`wellness-insight-chart-view-option ${selectedRange === 'all' ? 'selected-option' : ''}`}
                  onClick={() => setSelectedRange('all')}
                >
                  <span
                    className={`wellness-insight-chart-view-option-label ${selectedRange === 'all' ? 'selected' : ''}`}
                  >
                    {trans('ALL')}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-insight-chart-summary-row">
            <div className="modal-insight-chart-summary-item">
              <div className="modal-insight-chart-summary-item-label">
                <div className="modal-insight-chart-label" />
              </div>
              <div className="modal-insight-chart-summary-item-value-header">
                {/* <div className="modal-insight-chart-value avg-header">Avg</div> */}
                <div className="modal-insight-chart-value low-header">{trans('LOW')}</div>
                <div className="modal-insight-chart-value high-header">{trans('HIGH')}</div>
              </div>
            </div>
            {Object.keys(summaryData).map((m) => {
              const item = summaryData[m];
              if (typeof item === 'object') {
                return (
                  <div className="modal-insight-chart-summary-item" key={m}>
                    <div className="modal-insight-chart-summary-item-label">
                      <div className="modal-insight-chart-label">
                        {getTranslatedLabel(unitTitleData[userMeasurement][m])}
                      </div>
                    </div>
                    <div className="modal-insight-chart-summary-item-value">
                      {/* <div className="modal-insight-chart-value avg">
                        {item["AVG"] ?? item}
                      </div> */}
                      <div className="modal-insight-chart-value low">
                        {item['LOW'] ?? formatValue(item)}
                      </div>
                      <div className="modal-insight-chart-value high">
                        {item['HIGH'] ?? formatValue(item)}
                      </div>
                    </div>
                  </div>
                );
              }
              // if string => no data
              return (
                <div className="modal-insight-chart-summary-item" key={m}>
                  <div className="modal-insight-chart-summary-item-label">
                    <div className="modal-insight-chart-label">
                      {getTranslatedLabel(unitTitleData[userMeasurement][m])}
                    </div>
                  </div>
                  <div className="modal-insight-chart-summary-item-value">
                    {/* <div className="modal-insight-chart-value avg">{item}</div> */}
                    <div className="modal-insight-chart-value low">{formatValue(item)}</div>
                    <div className="modal-insight-chart-value high">{formatValue(item)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* CHART AREA */}
        <div className="modal-insight-chart-chart-card">
          {/* Loader if still loading or if insightData is not ready */}
          {loading || !graphData ? (
            <Spin spinning={true} style={{ margin: '70px -20px' }} />
          ) : modalTitle === 'blood_pressure' ? (
            <BloodPressureChart
              chartData={chartData}
              userTimezone={userTimezone}
              is24hoursformat={is24hoursformat}
              insightStartDateTime={insightStartDateTime}
              insightEndDateTime={insightEndDateTime}
              insightLoading={insightLoading}
              getDateFormatByLocation={getDateFormatByLocation}
              selectedRange={selectedRange}
            />
          ) : modalTitle === 'pulse_oximeter' ? (
            <PulseOximeterChart
              chartData={chartData}
              userTimezone={userTimezone}
              is24hoursformat={is24hoursformat}
              insightStartDateTime={insightStartDateTime}
              insightEndDateTime={insightEndDateTime}
              insightLoading={insightLoading}
              getDateFormatByLocation={getDateFormatByLocation}
              selectedRange={selectedRange}
            />
          ) : modalTitle === 'weight_scale' ? (
            <WeightScaleChart
              chartData={chartData}
              userTimezone={userTimezone}
              is24hoursformat={is24hoursformat}
              userDefinedMeasurement={userDefinedMeasurement}
              insightStartDateTime={insightStartDateTime}
              insightEndDateTime={insightEndDateTime}
              insightLoading={insightLoading}
              getDateFormatByLocation={getDateFormatByLocation}
              selectedRange={selectedRange}
            />
          ) : modalTitle === 'glucose_meter' ? (
            <GlucoseMeterChart
              chartData={chartData}
              userTimezone={userTimezone}
              is24hoursformat={is24hoursformat}
              insightStartDateTime={insightStartDateTime}
              insightEndDateTime={insightEndDateTime}
              insightLoading={insightLoading}
              getDateFormatByLocation={getDateFormatByLocation}
              selectedRange={selectedRange}
            />
          ) : modalTitle === 'thermometer' ? (
            <BodyTemperatureChart
              chartData={chartData}
              userTimezone={userTimezone}
              is24hoursformat={is24hoursformat}
              userDefinedMeasurement={userDefinedMeasurement}
              insightStartDateTime={insightStartDateTime}
              insightEndDateTime={insightEndDateTime}
              insightLoading={insightLoading}
              getDateFormatByLocation={getDateFormatByLocation}
              selectedRange={selectedRange}
            />
          ) : (
            <Line data={graphData} options={graphOptions} />
          )}
        </div>
      </div>
    </div>
  );
};

WellnessHistoryInsightChart.propTypes = {
  insightData: PropTypes.array,
  userMeasurement: PropTypes.string,
  modalTitle: PropTypes.string,
  timezone: PropTypes.string,
};

export default WellnessHistoryInsightChart;
